import React, { useEffect, useMemo, useState, ReactNode } from 'react';
import {
  vehicleService,
  Vehicle,
  maintenanceService,
  Maintenance,
  CreateMaintenanceData,
  Provider,
  providerService,
  getApiErrorMessage,
} from '../../api';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  formatCurrencyBRL,
  formatDatePtBR,
  parseDateFromInput,
  ptBRLocale,
  toDateInputValue,
  toISODateMiddayUTC,
  addDaysToDate,
} from '../../utils/formatters';

registerLocale('pt-BR', ptBRLocale);

import {
  Wrench,
  Search,
  Plus,
  Car,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  FileText,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  CheckCircle,
  Flag,
  XOctagon,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  ConfirmModal,
  Modal,
  Label,
  Select,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;
const MAX_MOTIVO = 200;

const statusLabelMap = {
  pending: 'Pendente',
  active: 'Aprovada',
  rejected: 'Rejeitada',
  concluded: 'Concluída',
} as const;

const statusClassMap = {
  pending: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  active: 'bg-blue-50 text-blue-800 ring-blue-600/20',
  rejected: 'bg-rose-50 text-rose-800 ring-rose-600/20',
  concluded: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
} as const;

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
};

const FieldWrapper = ({ children, error, label, htmlFor, required, hint }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {hint && !error ? (
      <p className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500">{hint}</p>
    ) : null}
    {error ? (
      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {error}
      </p>
    ) : null}
  </div>
);

const CharCounter = ({ value, max }: { value: string; max: number }) => (
  <span className={`shrink-0 text-[11px] font-semibold tabular-nums ${value.length > max ? 'text-rose-600' : 'text-slate-500'}`}>
    {value.length}/{max}
  </span>
);

const ManutencaoPage = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState<Maintenance | null>(null);
  const [formData, setFormData] = useState<Partial<CreateMaintenanceData>>({
    vehicle_id: undefined,
    provider_id: undefined,
    reason: '',
    entry_date: '',
    estimated_completion: '',
    final_price: undefined,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmData, setConfirmData] = useState<{
    isOpen: boolean;
    title: string;
    description?: string;
    variant?: 'danger' | 'primary';
    confirmText?: string;
    onConfirm?: () => Promise<void> | void;
  }>({ isOpen: false, title: '' });

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  useEffect(() => {
    fetchVehicles();
    fetchMaintenances();
    fetchProviders();
  }, []);

  const fetchVehicles = async () => {
    try {
      const res = await vehicleService.getAllVehicles();
      setVehicles(res || []);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar veículos'));
    }
  };

  const fetchMaintenances = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await maintenanceService.getOpen();
      setMaintenances(res);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar manutenções'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await providerService.getAll();
      setProviders(res || []);
    } catch (_err) {
      setProviders([]);
    }
  };

  const filteredMaintenances = useMemo(
    () => maintenances.filter(m => {
      if (!searchTerm.trim()) return true;
      const term = searchTerm.trim().toLowerCase();
      const vehicle = vehicles.find(v => v.id === m.vehicle_id);
      const provider = providers.find(p => p.id === m.provider_id);
      return (
        vehicle?.brand?.toLowerCase().includes(term) ||
        vehicle?.license_plate?.toLowerCase().includes(term) ||
        provider?.name?.toLowerCase().includes(term) ||
        m.reason?.toLowerCase().includes(term)
      );
    }),
    [maintenances, vehicles, providers, searchTerm],
  );

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredMaintenances.length / ITEMS_PER_PAGE)),
    [filteredMaintenances.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { maintenancesPaginados, pageRange } = useMemo(() => {
    const total = filteredMaintenances.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      maintenancesPaginados: filteredMaintenances.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredMaintenances, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const isVehicleUnderMaintenance = (vehicleId: number) => {
    return maintenances.some(m => m.id !== editingMaintenance?.id && m.vehicle_id === vehicleId);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: (name === 'vehicle_id' || name === 'provider_id' || name === 'final_price')
        ? (value === '' || value === null) ? undefined : Number(value)
        : value
    }));
    if (formErrors[name]) setFormErrors((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleDateChange = (field: 'entry_date' | 'estimated_completion', date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date ? toDateInputValue(date) : '',
    }));
    if (formErrors[field]) setFormErrors((p) => { const n = { ...p }; delete n[field]; return n; });
  };

  const handleCreate = () => {
    setFormData({ vehicle_id: undefined, provider_id: undefined, reason: '', entry_date: '', estimated_completion: '', final_price: undefined });
    setEditingMaintenance(null);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (m: Maintenance) => {
    setFormData({
      vehicle_id: m.vehicle_id,
      provider_id: m.provider_id,
      reason: m.reason,
      entry_date: m.entry_date.slice(0, 10),
      estimated_completion: m.estimated_completion.slice(0, 10),
      final_price: m.final_price,
    });
    setEditingMaintenance(m);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    setFormErrors({});
    setFormData({ vehicle_id: undefined, provider_id: undefined, reason: '', entry_date: '', estimated_completion: '', final_price: undefined });
    setEditingMaintenance(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.vehicle_id) nextErrors.vehicle_id = 'Selecione um veículo';
    if (!formData.provider_id) nextErrors.provider_id = 'Selecione um fornecedor';
    if (!formData.reason || String(formData.reason).trim().length < 3) nextErrors.reason = 'Informe o motivo (mín. 3 caracteres)';
    if (formData.reason && String(formData.reason).length > MAX_MOTIVO) nextErrors.reason = `Motivo muito longo (máx. ${MAX_MOTIVO})`;
    if (!formData.entry_date) nextErrors.entry_date = 'Informe a data de entrada';
    if (!formData.estimated_completion) nextErrors.estimated_completion = 'Informe a previsão de conclusão';
    if (formData.final_price === undefined || isNaN(Number(formData.final_price)) || Number(formData.final_price) < 0) {
      nextErrors.final_price = 'Informe um valor válido';
    }
    if (!editingMaintenance && formData.vehicle_id && isVehicleUnderMaintenance(formData.vehicle_id)) {
      nextErrors.vehicle_id = 'Este veículo já está em manutenção';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setToast({ type: 'error', message: 'Verifique os campos destacados em vermelho' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        entry_date: toISODateMiddayUTC(formData.entry_date!),
        estimated_completion: toISODateMiddayUTC(formData.estimated_completion!),
        final_price: Number(formData.final_price),
      };
      if (editingMaintenance) {
        await maintenanceService.update(editingMaintenance.id, payload);
        setToast({ type: 'success', message: 'Manutenção atualizada com sucesso!' });
      } else {
        await maintenanceService.create(payload as CreateMaintenanceData);
        setToast({ type: 'success', message: 'Manutenção criada com sucesso!' });
      }
      await fetchMaintenances();
      closeFormModal();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar manutenção'));
      setToast({ type: 'error', message: getApiErrorMessage(err, 'Erro ao salvar manutenção') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: number) => setConfirmData({
    isOpen: true,
    title: 'Excluir manutenção?',
    description: 'Essa ação é irreversível — a manutenção será removida permanentemente.',
    variant: 'danger',
    confirmText: 'Sim, excluir',
    onConfirm: async () => {
      setLoading(true);
      try {
        await maintenanceService.delete(id);
        await fetchMaintenances();
        setToast({ type: 'success', message: 'Manutenção excluída com sucesso!' });
      } catch (err: unknown) {
        setToast({ type: 'error', message: getApiErrorMessage(err, 'Erro ao excluir manutenção') });
      } finally { setLoading(false); setConfirmData(p => ({ ...p, isOpen: false })); }
    },
  });

  const handleApprove = (id: number) => setConfirmData({
    isOpen: true,
    title: 'Aprovar solicitação de manutenção?',
    description: 'A manutenção será marcada como aprovada.',
    confirmText: 'Aprovar',
    onConfirm: async () => {
      setLoading(true);
      try {
        await maintenanceService.approve(id);
        await fetchMaintenances();
        setToast({ type: 'success', message: 'Manutenção aprovada!' });
      } catch (err: unknown) {
        setToast({ type: 'error', message: getApiErrorMessage(err, 'Erro ao aprovar manutenção') });
      } finally { setLoading(false); setConfirmData(p => ({ ...p, isOpen: false })); }
    },
  });

  const handleReject = (id: number) => {
    const reason = window.prompt('Motivo da rejeição (opcional):') || undefined;
    setConfirmData({
      isOpen: true,
      title: 'Rejeitar esta manutenção?',
      description: reason ? `Motivo informado: ${reason}` : undefined,
      variant: 'danger',
      confirmText: 'Sim, rejeitar',
      onConfirm: async () => {
        setLoading(true);
        try {
          await maintenanceService.reject(id, reason);
          await fetchMaintenances();
          setToast({ type: 'success', message: 'Manutenção rejeitada!' });
        } catch (err: unknown) {
          setToast({ type: 'error', message: getApiErrorMessage(err, 'Erro ao rejeitar manutenção') });
        } finally { setLoading(false); setConfirmData(p => ({ ...p, isOpen: false })); }
      },
    });
  };

  const handleConclude = (id: number) => setConfirmData({
    isOpen: true,
    title: 'Concluir esta manutenção?',
    description: 'A manutenção será marcada como concluída e sairá desta lista.',
    confirmText: 'Sim, concluir',
    onConfirm: async () => {
      setLoading(true);
      try {
        await maintenanceService.conclude(id);
        await fetchMaintenances();
        setToast({ type: 'success', message: 'Manutenção concluída!' });
      } catch (err: unknown) {
        setToast({ type: 'error', message: getApiErrorMessage(err, 'Erro ao concluir manutenção') });
      } finally { setLoading(false); setConfirmData(p => ({ ...p, isOpen: false })); }
    },
  });

  const formatDate = (dateStr?: string) => formatDatePtBR(dateStr);

  const formatCurrency = (value?: number) => formatCurrencyBRL(value);

  return (
    <>
      <PageHeader title="Manutenção de Veículos" hideTitleInBody />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold leading-tight text-blue-600">Gestão de manutenções</p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
              Controle as manutenções programadas e corretivas.
              <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                Acompanhe o status de cada veículo da frota.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="primary" size="lg" onClick={handleCreate} disabled={submitting}>
              <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
              Nova Manutenção
            </Button>
          </div>
        </div>

        {error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Manutenções em aberto</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Solicitações pendentes, aprovadas e rejeitadas
                </p>
              </div>
              <div className="relative w-full sm:max-w-sm">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Buscar veículo, placa, fornecedor..."
                  className="pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && maintenances.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando manutenções...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[16%] min-w-[200px] max-w-[260px]">Veículo</Th>
                    <Th className="w-[14%] min-w-[170px] max-w-[230px]">Fornecedor</Th>
                    <Th className="w-[20%] min-w-[220px] max-w-[360px]">Motivo</Th>
                    <Th className="w-[10%] min-w-[120px] max-w-[150px]">Entrada</Th>
                    <Th className="w-[11%] min-w-[120px] max-w-[160px]">Previsão</Th>
                    <Th className="w-[9%] min-w-[110px] max-w-[140px]">Preço Final</Th>
                    <Th className="w-[9%] min-w-[100px] max-w-[130px]">Status</Th>
                    <Th className="w-[11%] min-w-[130px] max-w-[170px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {maintenancesPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Wrench className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhuma manutenção em aberto</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Clique em "Nova Manutenção" para cadastrar a primeira.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    maintenancesPaginados.map((m) => {
                      const vehicle = vehicles.find(v => v.id === m.vehicle_id);
                      const provider = providers.find(p => p.id === m.provider_id);
                      const statusLabel = statusLabelMap[m.status as keyof typeof statusLabelMap] || m.status;
                      const statusClasses = statusClassMap[m.status as keyof typeof statusClassMap] || '';
                      return (
                        <Tr key={m.id}>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={vehicle?.brand || '-'}>
                                      {vehicle?.brand || '-'}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                      {vehicle?.license_plate || 'Sem placa'}
                                    </span>
                                    {vehicle?.description ? <span className="truncate" title={vehicle.description}>· {vehicle.description}</span> : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[230px] overflow-hidden">
                              <span className="block truncate text-[13px] font-semibold leading-tight text-slate-900" title={provider?.name || '-'}>
                                {provider?.name || '-'}
                              </span>
                              {provider?.cnpj ? (
                                <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                                  CNPJ: {provider.cnpj}
                                </div>
                              ) : null}
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[360px] overflow-hidden">
                              <span className="block truncate text-[13px] font-medium leading-tight text-slate-800" title={m.reason}>
                                {m.reason}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                              <Calendar className="h-[15px] w-[15px] text-slate-400" />
                              {formatDate(m.entry_date)}
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                              <Clock className="h-[15px] w-[15px] text-slate-400" />
                              {formatDate(m.estimated_completion)}
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-[13px] font-bold text-slate-900 tabular-nums">
                              <DollarSign className="h-[15px] w-[15px] text-emerald-600" />
                              {formatCurrency(m.final_price)}
                            </div>
                          </Td>
                          <Td>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-bold ring-1 ring-inset ${statusClasses}`}>
                              {statusLabel}
                            </span>
                          </Td>
                          <Td>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(m)} title="Editar" className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200">
                                <Edit2 className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(m.id)} title="Excluir" className="h-8 w-8 rounded-lg text-slate-500 hover:text-rose-700 hover:bg-rose-50 hover:ring-rose-200">
                                <Trash2 className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                              {m.status === 'pending' && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => handleApprove(m.id)} title="Aprovar" className="h-8 w-8 rounded-lg text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 hover:ring-emerald-200">
                                    <CheckCircle className="h-4 w-4 stroke-[1.9]" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleReject(m.id)} title="Rejeitar" className="h-8 w-8 rounded-lg text-slate-500 hover:text-rose-700 hover:bg-rose-50 hover:ring-rose-200">
                                    <XOctagon className="h-4 w-4 stroke-[1.9]" />
                                  </Button>
                                </>
                              )}
                              {m.status === 'active' && (
                                <Button variant="ghost" size="icon" onClick={() => handleConclude(m.id)} title="Concluir" className="h-8 w-8 rounded-lg text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 hover:ring-emerald-200">
                                  <Flag className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' manutenção(ões)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isModalOpen && (
        <Modal
          onClose={closeFormModal}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-indigo-700 text-white shadow-[0_6px_16px_-8px_rgba(37,99,235,0.6)]">
                <Wrench className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  {editingMaintenance ? 'Editar manutenção' : 'Nova solicitação de manutenção'}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Preencha os campos abaixo para {editingMaintenance ? 'atualizar os dados' : 'solicitar uma nova manutenção'}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button type="button" onClick={closeFormModal} disabled={submitting} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60">
                Cancelar
              </button>
              <button type="submit" form="manutencao-form" disabled={submitting} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-indigo-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(37,99,235,0.65)] focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Salvando...</> : editingMaintenance ? 'Salvar alterações' : 'Salvar'}
              </button>
            </div>
          }
        >
          <form id="manutencao-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados do veículo e fornecedor</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo</span>} htmlFor="manutencao-veiculo" required error={formErrors.vehicle_id}>
                    <Select id="manutencao-veiculo" name="vehicle_id" value={formData.vehicle_id || ''} onChange={handleInputChange}>
                      <option value="">Selecione um veículo</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id} disabled={isVehicleUnderMaintenance(vehicle.id)}>
                          {vehicle.brand}{vehicle.license_plate ? ` — ${vehicle.license_plate}` : ''}{vehicle.description ? ` (${vehicle.description})` : ''}{isVehicleUnderMaintenance(vehicle.id) ? ' (em manutenção)' : ''}
                        </option>
                      ))}
                    </Select>
                  </FieldWrapper>
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Fornecedor / Oficina</span>} htmlFor="manutencao-provider" required error={formErrors.provider_id}>
                    <Select id="manutencao-provider" name="provider_id" value={formData.provider_id || ''} onChange={handleInputChange}>
                      <option value="">Selecione o fornecedor</option>
                      {providers.length === 0 ? <option value="" disabled>Nenhum fornecedor cadastrado</option> : providers.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Select>
                  </FieldWrapper>
                </div>
                <FieldWrapper label={<span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Motivo / Descrição</span>} htmlFor="manutencao-motivo" required error={formErrors.reason}>
                  <div className="relative">
                    <Input id="manutencao-motivo" name="reason" value={formData.reason || ''} onChange={handleInputChange} placeholder="Troca de óleo, revisão de freios, conserto..." className="pr-20" maxLength={MAX_MOTIVO + 50} />
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <CharCounter value={formData.reason || ''} max={MAX_MOTIVO} />
                    </div>
                  </div>
                </FieldWrapper>
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Datas e orçamento</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Data de entrada</span>} htmlFor="manutencao-entry" required error={formErrors.entry_date}>
                    <DatePicker
                      selected={formData.entry_date ? parseDateFromInput(formData.entry_date) : null}
                      onChange={(date) => handleDateChange('entry_date', date)}
                      locale="pt-BR"
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      calendarStartDay={0}
                      className={`w-full h-10 rounded-lg border px-3.5 text-[13px] text-slate-900 outline-none transition ${formErrors.entry_date ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10' : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:ring-offset-0'}`}
                      placeholderText="Selecione a data"
                    />
                  </FieldWrapper>
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Previsão de conclusão</span>} htmlFor="manutencao-estimate" required error={formErrors.estimated_completion}>
                    <DatePicker
                      selected={formData.estimated_completion ? parseDateFromInput(formData.estimated_completion) : null}
                      onChange={(date) => handleDateChange('estimated_completion', date)}
                      locale="pt-BR"
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      calendarStartDay={0}
                      minDate={
                        formData.entry_date
                          ? addDaysToDate(parseDateFromInput(formData.entry_date), 1) ?? undefined
                          : undefined
                      }
                      className={`w-full h-10 rounded-lg border px-3.5 text-[13px] text-slate-900 outline-none transition ${formErrors.estimated_completion ? 'border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10' : 'border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:ring-offset-0'}`}
                      placeholderText="Selecione a data"
                    />
                  </FieldWrapper>
                </div>
                <FieldWrapper label={<span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Preço final (R$)</span>} htmlFor="manutencao-price" required error={formErrors.final_price} hint="Orçamento ou valor já pago pela manutenção">
                  <Input id="manutencao-price" type="number" name="final_price" value={formData.final_price === undefined ? '' : formData.final_price} onChange={handleInputChange} placeholder="0,00" min="0" step="0.01" />
                </FieldWrapper>
              </div>
            </div>
          </form>
        </Modal>
      )}

      <ConfirmModal isOpen={confirmData.isOpen} onCancel={() => setConfirmData(p => ({ ...p, isOpen: false }))} onConfirm={() => confirmData.onConfirm && confirmData.onConfirm()} title={confirmData.title} description={confirmData.description || 'Esta ação é permanente.'} confirmLabel={confirmData.confirmText || 'Confirmar'} confirmVariant={confirmData.variant as any} loading={loading} />
    </>
  );
};

export default ManutencaoPage;
